import axios from 'axios';
import {
  ManagedUser,
  UserRoleSummary,
  UpdateManagedUserData,
  InviteUserData,
} from '../types/auth.types';
import { OrgPage, OrgApiParams } from './organization.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token might be expired, redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Snake_case API payload -> camelCase app model transforms
const toUserRoleSummary = (role: any): UserRoleSummary => ({
  id: role.id,
  role: role.role,
  organizationType: role.organization_type,
  organizationId: role.organization_id,
  organizationName: role.organization_name,
});

const toManagedUser = (user: any): ManagedUser => ({
  id: user.id,
  email: user.email,
  firstName: user.first_name || '',
  lastName: user.last_name || '',
  fullName: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
  phoneNumber: user.phone_number,
  avatar: user.avatar,
  isActive: user.is_active,
  createdAt: user.created_at,
  updatedAt: user.updated_at,
  defaultCompany: user.default_company,
  defaultCompanyId: user.default_company_id,
  defaultCompanyName: user.default_company_name,
  defaultTeam: user.default_team,
  defaultTeamId: user.default_team_id,
  defaultTeamName: user.default_team_name,
  defaultDepartment: user.default_department,
  defaultDepartmentId: user.default_department_id,
  defaultDepartmentName: user.default_department_name,
  roles: (user.roles ?? []).map(toUserRoleSummary),
});

// CamelCase app model -> snake_case API write payload transforms. DRF only
// binds its serializer field names, so camelCase keys like isActive are
// silently dropped on POST/PATCH — every write must go through these.
const USER_WRITE_RENAMES: Record<string, string> = {
  firstName: 'first_name',
  lastName: 'last_name',
  isActive: 'is_active',
};

export const toUserPayload = (data: UpdateManagedUserData): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) continue;
    payload[USER_WRITE_RENAMES[key] ?? key] = value;
  }
  return payload;
};

export const toInvitePayload = (data: InviteUserData): Record<string, unknown> => ({
  email: data.email,
  first_name: data.firstName,
  last_name: data.lastName,
});

class UserService {
  async getUsersPage(params: OrgApiParams): Promise<OrgPage<ManagedUser>> {
    const response = await api.get('/auth/users/', { params });
    const data = response.data;
    return {
      count: data.count,
      next: data.next,
      previous: data.previous,
      results: (data.results ?? []).map(toManagedUser),
    };
  }

  async getUser(id: string): Promise<ManagedUser> {
    const response = await api.get(`/auth/users/${id}/`);
    return toManagedUser(response.data);
  }

  async updateUser(id: string, data: UpdateManagedUserData): Promise<ManagedUser> {
    const response = await api.patch(`/auth/users/${id}/`, toUserPayload(data));
    return toManagedUser(response.data);
  }

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/auth/users/${id}/`);
  }

  async inviteUser(data: InviteUserData): Promise<ManagedUser> {
    const response = await api.post('/auth/users/invite/', toInvitePayload(data));
    return toManagedUser(response.data);
  }
}

const userService = new UserService();
export default userService;
